import { useState, type ReactNode, useMemo } from 'react'
import { DateTime } from 'luxon'
import { parseAsCompanyId, parseAsTeamId, parseAsUserId } from '@util'
import {
  type CompanyId,
  isDefined,
  isEmpty,
  isTeamId,
  type SmallCloser,
  type TeamId,
  type UserId,
  type UserProfileData,
} from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { Alert, Chip, Divider, Group, Loader, NumberInput, Select, Stack, Tabs, Text } from '@mantine/core'
import { useDebounce, useStoredNullableQuery } from '@hooks'
import {
  CLOSER_MODELS,
  CLOSER_STATUS_ITEMS,
  Size,
  translateLabels,
  useEntityClosers,
  usePermissionCheck,
  useProfile,
  useTeams,
  useTimeslots,
  useTranslation,
} from '@front/model'
import {
  CompanyInlineSelect,
  NotReady,
  OptionalRow,
  Page,
  PreferenceFormProvider,
  PreferenceNumberInput,
  PreferenceSelect,
  PreferenceSwitch,
  TButton,
  TDrawer,
  TListItem,
  TSection,
  TSectionList,
  TStack,
  UserInfo,
  UserInlineSelect,
} from '@components'

function TimeSlotsPage(): ReactNode {
  const { t } = useTranslation()
  const { user, isAdmin, isTantalim, isLoading, error } = useProfile()
  if (!user || isLoading) return <NotReady data={user} error={error} id='profile' isLoading={isLoading} />
  if (!isTantalim) return <NotReady error={t('Invalid request')} />

  return (
    <Page>
      <Page.Heading title={t('Closer Availability')} />
      <Page.Content scrollArea>
        <Content self={user} />
      </Page.Content>
    </Page>
  )
}

const Content = ({ self }: { self: UserProfileData }): ReactNode => {
  const { t } = useTranslation()
  const { isTantalim } = useProfile()
  const { value: companyParam, setValue: setCompanyId } = useStoredNullableQuery<CompanyId>({
    storedKey: 'Closers|companyId',
    urlKey: 'companyId',
    parser: parseAsCompanyId,
  })
  const companyId = companyParam ?? self.companyId
  const { value: teamId, setValue: setTeamId } = useStoredNullableQuery<TeamId>({
    storedKey: 'Closers|teamId',
    urlKey: 'teamId',
    parser: parseAsTeamId,
  })
  const { value: userId, setValue: setUserId } = useStoredNullableQuery<UserId>({
    storedKey: 'Closers|userId',
    urlKey: 'userId',
    parser: parseAsUserId,
  })

  const { data: teams } = useTeams({ companyId, enabled: isDefined(companyId) })

  const entityId = userId || teamId || companyId || 'C.MISSING'
  const { data: closers } = useEntityClosers({ entityId })
  const [selectedCloser, setSelectedCloser] = useState<SmallCloser>()

  const closerStatusItems = useMemo(() => translateLabels(CLOSER_STATUS_ITEMS, t), [t])
  const closerModels = useMemo(() => translateLabels(CLOSER_MODELS, t), [t])

  const { data: isAllowedUpdate } = usePermissionCheck({ permissionType: 'company:update', targetId: companyId })

  return (
    <Stack>
      <Group>
        {isTantalim && (
          <CompanyInlineSelect
            label={t('Company')}
            placeholder={t('Select company')}
            value={companyId}
            onChange={(value) => {
              if (isDefined(value)) {
                setCompanyId(value)
                setTeamId(null)
                setUserId(null)
              }
            }}
          />
        )}
        <Select
          allowDeselect
          searchable
          label={t('Team')}
          placeholder={t('Select team')}
          value={teamId}
          data={teams?.map((t) => ({
            value: t.teamId,
            label: t.name,
          }))}
          onChange={(value) => {
            if (isTeamId(value)) {
              setTeamId(value)
              setUserId(null)
            } else {
              setTeamId(null)
            }
          }}
        />
        <UserInlineSelect
          teamIds={teamId ? [teamId] : undefined}
          allowDeselect
          value={userId || undefined}
          onChange={(value) => {
            setUserId(value ?? null)
          }}
          label={t('User')}
          companyId={companyId}
        />
      </Group>
      <TSection heading={{ title: t('Company Preferences') }}>
        <PreferenceFormProvider allowUpdate={Boolean(isAllowedUpdate)} defaultGroup='Calendar'>
          <PreferenceSelect data={closerStatusItems} preference='closerStatus' targetId={companyId} />
          <PreferenceSelect data={closerModels} preference='closerModel' targetId={companyId} />
          <PreferenceSwitch preference='restrictSetterCloseEvents' targetId={companyId} />
          <PreferenceNumberInput
            allowDecimal={false}
            min={0}
            placeholder='100%'
            preference='bookingPercent'
            suffix='%'
            targetId={companyId}
          />
        </PreferenceFormProvider>
      </TSection>

      <TSectionList heading={{ title: t('Closers') }}>
        {closers && isEmpty(closers) && <Alert variant='filled'>{t('No closers')}</Alert>}
        {closers?.map((closer) => (
          <TButton
            justify='start'
            key={closer.userId}
            size='medium'
            variant='ghost'
            onClick={() => setSelectedCloser(closer)}
          >
            <UserInfo {...closer} linked={false} />
          </TButton>
        ))}
      </TSectionList>
      <TDrawer
        opened={isDefined(selectedCloser)}
        size='xl'
        title={selectedCloser ? <UserInfo linked={false} {...selectedCloser} /> : t('Closer')}
        onClose={() => setSelectedCloser(undefined)}
      >
        <Tabs defaultValue='timeslots'>
          <Tabs.List>
            <Tabs.Tab value='basic'>{t('Basic Info')}</Tabs.Tab>
            <Tabs.Tab value='timeslots'>{t('Check Timeslots')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value='basic'>
            <Stack>
              {selectedCloser && (
                <Stack p={Size.space.xl}>
                  <CloserInfo closer={selectedCloser} />
                </Stack>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value='timeslots'>
            <Stack p={Size.space.xl}>{selectedCloser && <GetCloserTimeslots closer={selectedCloser} />}</Stack>
          </Tabs.Panel>
        </Tabs>
      </TDrawer>
    </Stack>
  )
}

const CloserInfo = ({ closer }: { closer: SmallCloser }): ReactNode => {
  const { t } = useTranslation()
  const closerTimeslots = Object.entries(closer.timeslots || {})
  return (
    <>
      <TStack divider={<Divider />}>
        {Object.entries(closer)
          .filter(([keys, value]) => typeof value !== 'object')
          .map(([key, value]) => (
            <OptionalRow key={key} label={key} value={typeof value !== 'object' ? value : '--'} />
          ))}
      </TStack>
      <TSection heading={{ title: t('Closer Setting') }}>
        {isEmpty(closerTimeslots) && <Alert>{t("Cannot find user's closer settings")}</Alert>}
        {closerTimeslots.map(([day, times], index) => {
          const startOfDay = DateTime.fromObject({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          return (
            <TListItem detailBold key={day} label={day}>
              <Group wrap='wrap'>
                {times.map((time) => (
                  <div key={time}>{startOfDay.plus({ minutes: time }).toLocaleString(DateTime.TIME_SIMPLE)}</div>
                ))}
              </Group>
            </TListItem>
          )
        })}
      </TSection>
    </>
  )
}

const GetCloserTimeslots = ({ closer }: { closer: SmallCloser }): ReactNode => {
  const { t } = useTranslation()
  const [days, setDays] = useState<number>(28)
  const from = DateTime.now().startOf('day')
  const to = from.plus({ days })
  const debouncedDays = useDebounce(days, 500)

  const { data: timeslots, isFetching } = useTimeslots({ targetId: closer.userId, days: debouncedDays })
  const userTimeslots = timeslots?.[0]?.times ?? []
  const groupDays = userTimeslots.reduce<Record<string, number[]>>((acc, time) => {
    const day = DateTime.fromMillis(time.startTime).toFormat('ccc LLL dd')
    acc[day] = acc[day] ?? []
    acc[day].push(time.startTime)
    return acc
  }, {})

  return (
    <>
      <NumberInput
        description={`${from.toLocaleString(DateTime.DATE_MED)} - ${to.toLocaleString(DateTime.DATE_MED)}`}
        label={t('Days')}
        min={1}
        value={days}
        onChange={(value) => {
          const v = Number(value)
          if (v < 0) return
          setDays(v)
        }}
      />
      {isEmpty(userTimeslots) && !isFetching && <Alert>{t('No timeslots')}</Alert>}
      {isFetching && <Loader />}
      {Object.entries(groupDays).map(([day, times], index) => {
        return (
          <Stack key={day}>
            <Text>{day}</Text>
            <Group wrap='wrap'>
              {times.map((time) => (
                <Chip key={time}>{DateTime.fromMillis(time).toLocaleString(DateTime.TIME_SIMPLE)}</Chip>
              ))}
            </Group>
          </Stack>
        )
      })}
    </>
  )
}

export const Route = createFileRoute('/closers/')({
  component: TimeSlotsPage,
})
